import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  AutoAwesome as SparkleIcon,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const FeaturesPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const features = [
    {
      name: t("features.chatConnect.name"),
      smallIcon: "/assets/icon/chat-connect-logo.png",
      largeImage: "/assets/img/messages-concept.jpg",
      description: t("features.chatConnect.description"),
      details: t("features.chatConnect.details", { returnObjects: true }),
    },
    {
      name: t("features.shiftConnect.name"),
      smallIcon: "/assets/icon/shift-connect-logo.png",
      largeImage: "/assets/img/clock-time.jpg",
      description: t("features.shiftConnect.description"),
      details: t("features.shiftConnect.details", { returnObjects: true }),
    },
    {
      name: t("features.wageConnect.name"),
      smallIcon: "/assets/icon/wage-connect.png",
      largeImage: "/assets/img/finance-department.jpg",
      description: t("features.wageConnect.description"),
      details: t("features.wageConnect.details", { returnObjects: true }),
    },
    {
      name: t("features.bookingConnect.name"),
      smallIcon: "/assets/icon/BookConnect-logo.png",
      largeImage: "/assets/img/business-planning.jpg",
      description: t("features.bookingConnect.description"),
      details: t("features.bookingConnect.details", { returnObjects: true }),
    },
    {
      name: t("features.taskConnect.name"),
      smallIcon: "/assets/icon/tc-icon.png",
      largeImage: "/assets/img/team-members.jpg",
      description: t("features.taskConnect.description"),
      details: t("features.taskConnect.details", { returnObjects: true }),
    },
    {
      name: t("features.clientConnect.name"),
      smallIcon: "/assets/icon/ClientConnect-logo.png",
      largeImage: "/assets/img/client-management.jpg",
      description: t("features.clientConnect.description"),
      details: t("features.clientConnect.details", { returnObjects: true }),
    },
    {
      name: t("features.memberConnect.name"),
      smallIcon: "/assets/icon/MemberConnect-logo.png",
      largeImage: "/assets/img/team-members.jpg",
      description: t("features.memberConnect.description"),
      details: t("features.memberConnect.details", { returnObjects: true }),
    },
  ];

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % features.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + features.length) % features.length);
  };

  const getCardTransform = (index) => {
    const position = (index - currentIndex + features.length) % features.length;

    if (position === 0) {
      return {
        x: 0,
        y: 0,
        scale: 1,
        zIndex: 50,
        opacity: 1,
        rotateY: 0,
      };
    } else if (position === 1) {
      return {
        x: 30,
        y: 30,
        scale: 0.95,
        zIndex: 40,
        opacity: 0.7,
        rotateY: -5,
      };
    } else if (position === 2) {
      return {
        x: 60,
        y: 60,
        scale: 0.9,
        zIndex: 30,
        opacity: 0.4,
        rotateY: -10,
      };
    } else {
      return {
        x: 90,
        y: 90,
        scale: 0.85,
        zIndex: 20,
        opacity: 0,
        rotateY: -15,
      };
    }
  };

  const currentFeature = features[currentIndex];

  return (
    <Box
      id="feature"
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #d8e1f0ff 100%)",
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        py: { xs: 6, md: 10 },
      }}
    >
      {/* Subtle animated background */}
      <Box
        sx={{
          position: "absolute",
          top: "15%",
          left: "10%",
          width: "400px",
          height: "400px",
          background:
            "radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          animation: "float 8s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "20%",
          right: "10%",
          width: "500px",
          height: "500px",
          background:
            "radial-gradient(circle, rgba(236, 72, 153, 0.06) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(80px)",
          animation: "float 10s ease-in-out infinite reverse",
        }}
      />

      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <Box sx={{ textAlign: "center", mb: { xs: 4, md: 8 } }}>
            <Chip
              icon={<SparkleIcon sx={{ color: "#64748b !important" }} />}
              label={t("features.keyFeature")}
              sx={{
                mb: 2,
                px: 2,
                py: 2.5,
                height: "auto",
                background: "#fff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                "& .MuiChip-label": {
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#64748b",
                },
              }}
            />
            <Typography
              variant={isMobile ? "h4" : "h2"}
              component="h1"
              sx={{
                fontWeight: 800,
                color: "#1e293b",
                mb: 2,
                letterSpacing: "-0.02em",
              }}
            >
              {t("features.title")}
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              sx={{
                color: "#64748b",
                maxWidth: 700,
                mx: "auto",
                fontWeight: 400,
                lineHeight: 1.7,
                px: { xs: 2, sm: 0 },
              }}
            >
              {t("features.description")}
            </Typography>
          </Box>
        </motion.div>

        {/* Main Layout: Left Content + Right Cards */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 4, lg: 6 },
            alignItems: { xs: "stretch", lg: "center" },
            maxWidth: "1300px",
            mx: "auto",
          }}
        >
          {/* Left: Feature Details */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Box sx={{ order: { xs: 2, lg: 1 }, flex: { lg: "1" } }}>
              {/* Feature Icon & Title */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 2, md: 3 },
                  mb: { xs: 3, md: 4 },
                }}
              >
                <Box
                  sx={{
                    p: { xs: 1.5, md: 2 },
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={currentFeature.smallIcon}
                    alt={currentFeature.name}
                    style={{
                      width: isMobile ? "32px" : "40px",
                      height: isMobile ? "32px" : "40px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant={isMobile ? "h5" : "h3"}
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      lineHeight: 1.2,
                    }}
                  >
                    {currentFeature.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#94a3b8", mt: 0.5, display: "block" }}
                  >
                    {t("features.featureCounter", {
                      current: currentIndex + 1,
                      total: features.length,
                    })}
                  </Typography>
                </Box>
              </Box>

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "0.95rem", md: "1.1rem" },
                  lineHeight: 1.7,
                  color: "#475569",
                  mb: { xs: 3, md: 4 },
                }}
              >
                {currentFeature.description}
              </Typography>

              {/* Details List */}
              <Box sx={{ space: 2 }}>
                {currentFeature.details.map((detail, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08, duration: 0.5 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: { xs: 1.5, md: 2 },
                        p: { xs: 2, md: 2.5 },
                        mb: { xs: 1.5, md: 2 },
                        borderRadius: 2,
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        width: { xs: "100%", md: "35rem" },
                        transition: "all 0.3s ease",
                        "&:hover": {
                          borderColor: "#cbd5e1",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          mt: 0.5,
                          p: 0.5,
                          borderRadius: "50%",
                          background: "#f1f5f9",
                          display: "flex",
                          flexShrink: 0,
                        }}
                      >
                        <CheckIcon
                          sx={{
                            fontSize: { xs: 16, md: 18 },
                            color: "#64748b",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#475569",
                          fontSize: { xs: "0.9rem", md: "1rem" },
                          lineHeight: 1.6,
                          fontWeight: 500,
                        }}
                      >
                        {detail}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>

              {/* Navigation */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mt: { xs: 3, md: 4 },
                }}
              >
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    p: { xs: 1.2, md: 1.5 },
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "#f8fafc",
                      borderColor: "#cbd5e1",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <ChevronLeft
                    sx={{ color: "#475569", fontSize: { xs: 22, md: 24 } }}
                  />
                </IconButton>
                <IconButton
                  onClick={handleNext}
                  sx={{
                    p: { xs: 1.2, md: 1.5 },
                    background: "#1e293b",
                    boxShadow: "0 4px 12px rgba(30,41,59,0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "#334155",
                      boxShadow: "0 6px 16px rgba(30,41,59,0.4)",
                    },
                  }}
                >
                  <ChevronRight
                    sx={{ color: "#fff", fontSize: { xs: 22, md: 24 } }}
                  />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#94a3b8",
                    ml: 1,
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {t("features.swipeToExplore")}
                </Typography>
              </Box>
            </Box>
          </motion.div>

          {/* Right: Stacked Cards */}
          <Box
            sx={{
              position: "relative",
              height: { xs: 520, sm: 580, md: 620 },
              order: { xs: 1, lg: 2 },
              flex: { lg: "1.1" },
              marginLeft: { xs: -2, lg: 20 },
              perspective: "1500px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pb: { xs: 6, lg: 0 },
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AnimatePresence initial={false}>
                {features.map((feature, index) => {
                  const transform = getCardTransform(index);
                  const isActive = index === currentIndex;

                  return (
                    <motion.div
                      key={index}
                      style={{
                        position: "absolute",
                        width: "100%",
                        maxWidth: isMobile ? "90%" : "480px",
                        cursor: isActive ? "pointer" : "default",
                        transformStyle: "preserve-3d",
                      }}
                      animate={transform}
                      transition={{
                        duration: 0.6,
                        ease: [0.32, 0.72, 0, 1],
                      }}
                      onClick={isActive ? handleNext : undefined}
                    >
                      <Card
                        sx={{
                          borderRadius: { xs: 3, md: 4 },
                          background: "#fff",
                          boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
                          border: "1px solid #e2e8f0",
                          overflow: "hidden",
                          pointerEvents: isActive ? "auto" : "none",
                        }}
                      >
                        {/* Card Header */}
                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: { xs: 3, md: 4 },
                            }}
                          >
                            <Box
                              sx={{
                                width: { xs: 56, md: 64 },
                                height: { xs: 56, md: 64 },
                                borderRadius: 3,
                                background:
                                  "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                              }}
                            >
                              <img
                                src={feature.smallIcon}
                                alt={feature.name}
                                style={{
                                  width: isMobile ? "28px" : "32px",
                                  height: isMobile ? "28px" : "32px",
                                  objectFit: "contain",
                                }}
                              />
                            </Box>
                            <Box sx={{ textAlign: "right" }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#94a3b8",
                                  fontWeight: 600,
                                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                                }}
                              >
                                {t("features.featureLabel")}
                              </Typography>
                              <Typography
                                variant="h4"
                                sx={{
                                  fontWeight: 700,
                                  color: "#1e293b",
                                  fontSize: { xs: "1.5rem", md: "2rem" },
                                }}
                              >
                                {String(index + 1).padStart(2, "0")}
                              </Typography>
                            </Box>
                          </Box>

                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              color: "#1e293b",
                              mb: { xs: 1.5, md: 2 },
                              fontSize: { xs: "1.1rem", md: "1.5rem" },
                            }}
                          >
                            {feature.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#64748b",
                              lineHeight: 1.6,
                              fontSize: { xs: "0.875rem", md: "1rem" },
                            }}
                          >
                            {feature.description}
                          </Typography>
                        </CardContent>

                        {/* Card Visual */}
                        <Box
                          sx={{ px: { xs: 3, md: 4 }, pb: { xs: 3, md: 4 } }}
                        >
                          <Box
                            sx={{
                              height: { xs: 220, md: 260 },
                              background:
                                "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                              borderRadius: 3,
                              border: "1px solid #e2e8f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={feature.largeImage}
                              alt={feature.name}
                              style={{
                                height: "100%",
                                width: "100%",
                                objectFit: "cover",
                                borderRadius: "3px",
                              }}
                            />
                          </Box>
                        </Box>

                        {/* Card Footer */}
                        <Box
                          sx={{
                            px: { xs: 3, md: 4 },
                            pb: { xs: 3, md: 4 },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#94a3b8",
                              fontSize: { xs: "0.8rem", md: "0.875rem" },
                            }}
                          >
                            {t("features.clickOrSwipe")}
                          </Typography>
                          <ChevronRight
                            sx={{
                              color: "#cbd5e1",
                              fontSize: { xs: 20, md: 24 },
                            }}
                          />
                        </Box>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              {/* Indicator Dots */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 1.5,
                  zIndex: 100,
                }}
              >
                {features.map((_, index) => (
                  <Box
                    key={index}
                    component="button"
                    onClick={() => setCurrentIndex(index)}
                    sx={{
                      width: index === currentIndex ? 32 : 8,
                      height: 8,
                      borderRadius: 4,
                      border: "none",
                      background:
                        index === currentIndex ? "#1e293b" : "#cbd5e1",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background:
                          index === currentIndex ? "#334155" : "#94a3b8",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          33% {
            transform: translateY(-20px) translateX(10px);
          }
          66% {
            transform: translateY(-10px) translateX(-10px);
          }
        }
      `}</style>
    </Box>
  );
};

export default FeaturesPage;
